import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, buildOAuthLoginUrl } from '../services/api';
import { useTranslation } from 'react-i18next';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const { sendEmailLink } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [googleBlocked, setGoogleBlocked] = useState(false);

  useEffect(() => {
    let active = true;
    authAPI.getGeoInfo()
      .then((res) => {
        if (active) setGoogleBlocked(Boolean(res.data?.google_oauth_blocked));
      })
      .catch(() => {
        // fail-open: leave Google enabled on any geo lookup error
      });
    return () => { active = false; };
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('login.error_empty_email'));
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      await sendEmailLink(email);
      setEmailSent(true);
    } catch (err) {
      setError(t('login.error_send_link'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    if (googleBlocked) return;
    const googleAuthUrl = buildOAuthLoginUrl('google');
    console.log('Google login URL:', googleAuthUrl);
    window.location.href = googleAuthUrl;
  };

  const handleYandexLogin = () => {
    const yandexAuthUrl = buildOAuthLoginUrl('yandex');
    console.log('Yandex login URL:', yandexAuthUrl);
    window.location.href = yandexAuthUrl;
  };
  
  if (emailSent) {
    return (
      <div className="login-page">
        <div className="login-container">
          <h1>{t('email_sent.title')}</h1>
          <p className="login-description">
            {t('email_sent.description', { email })}
          </p>
          <button
            className="submit-button"
            onClick={() => setEmailSent(false)}
          >
            {t('email_sent.back_button')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="login-page">
      <div className="login-container">
        <h1>{t('login.title')}</h1>
        <p className="login-description">
          {t('login.description')}
        </p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleEmailLogin} className="email-form">
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('login.email_placeholder')}
            disabled={isLoading}
            className="email-input"
          />
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? t('login.loading') : t('login.submit_button')}
          </button>
        </form>
        
        <div className="divider">
          <span>{t('login.divider')}</span>
        </div>

        <div className="oauth-buttons">
          <button
            className="oauth-button google"
            onClick={handleGoogleLogin}
            disabled={isLoading || googleBlocked}
          >
            <img src="/google-icon.svg" alt="Google" />
            {t('login.google_button')}
          </button>

          {googleBlocked && (
            <p className="oauth-blocked-notice">
              {t('login.google_blocked_notice')}
            </p>
          )}

          {(i18n.language === 'ru' || googleBlocked) && (
            <button
              className="oauth-button yandex"
              onClick={handleYandexLogin}
              disabled={isLoading}
            >
              <img src="/yandex-icon.svg" alt="Yandex" />
              {t('login.yandex_button')}
            </button>
          )}
        </div>

        <p className="terms-text">
          {t('login.terms_prefix')} <Link to="/terms">{t('login.terms_link')}</Link> {t('login.terms_and')} <Link to="/privacy">{t('login.privacy_link')}</Link>.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
